import { useQuery } from '@tanstack/react-query';
import { listMyBookings } from '../lib/api';
import { useRouter } from '../lib/router';
import { useT } from '../lib/i18n';
import { Card, CardHint, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { RetryCard } from '../components/RetryCard';
import { PullIndicator } from '../components/PullIndicator';
import { usePullToRefresh } from '../lib/usePullToRefresh';

export function MyBookingsPage(): JSX.Element {
  const { navigate } = useRouter();
  const { t } = useT();
  const q = useQuery({ queryKey: ['my-bookings'], queryFn: listMyBookings });
  const ptr = usePullToRefresh({ onRefresh: () => q.refetch() });

  return (
    <div
      {...ptr.bind}
      style={{ transform: `translateY(${ptr.offset}px)`, transition: ptr.offset === 0 ? 'transform 200ms ease-out' : undefined }}
      className="relative mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6"
    >
      <PullIndicator offset={ptr.offset} refreshing={ptr.refreshing} />
      <h1 className="mb-4 text-2xl font-bold text-tg-text">{t('my.bookings.title')}</h1>
      {q.isLoading && (
        <div className="space-y-3">
          <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
          <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
          <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
        </div>
      )}
      {q.isError && (
        <RetryCard
          message={(q.error as Error).message}
          onRetry={() => q.refetch()}
          isRetrying={q.isFetching}
        />
      )}
      {q.data && q.data.items.length === 0 && (
        <Card className="space-y-3 text-center">
          <CardTitle>{t('my.bookings.empty.t')}</CardTitle>
          <CardHint>{t('my.bookings.empty.b')}</CardHint>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mx-auto inline-flex min-h-[44px] items-center rounded-pill bg-brand-green px-4 text-sm font-semibold text-white"
          >
            {t('home.search')}
          </button>
        </Card>
      )}
      <div className="space-y-3">
        {q.data?.items.map((b) => {
          const d = b.ride?.departureAt ? new Date(b.ride.departureAt) : null;
          return (
            <Card key={b.id} onClick={() => navigate(`/booking/${b.id}`)} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-tg-text">
                  {b.ride ? `${b.ride.originName} → ${b.ride.destinationName}` : t('my.bookings.id', { id: b.id.slice(-6) })}
                </p>
                <Badge
                  tone={
                    b.status === 'pending'
                      ? 'warning'
                      : b.status === 'approved'
                        ? 'success'
                        : b.status === 'completed' || b.status === 'rated'
                          ? 'neutral'
                          : b.status === 'declined' || b.status.startsWith('cancelled')
                            ? 'danger'
                            : 'neutral'
                  }
                >
                  {t(`bs.${b.status}` as 'bs.pending')}
                </Badge>
              </div>
              <p className="text-sm text-tg-hint">
                {d
                  ? `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · `
                  : ''}
                {b.seats} {b.seats === 1 ? t('common.seat_one') : t('common.seat_other')}
                {b.ride ? ` · ${Math.round(b.ride.proposedCost)} ${b.ride.currency}` : ''}
              </p>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
